import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, TrendingUp, AlertCircle, Play, Download, BookOpen } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { generateIncomeStatementPDF } from '../../../utils/pdfExport';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';
import ReportPeriodSelector, { REPORT_MONTHS, getMonthDateRange, getYearDateRange } from '../../reports/ReportPeriodSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function IncomeStatementTab({ fmt, viewingStudentId, viewingStudentName, onTraceGl }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const initialRange = getMonthDateRange(initialYear, initialMonth);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [data, setData] = useState(null);
    const [reportType, setReportType] = useState('period'); // period, comparative, ytd, as_of
    const [compareType] = useState('prev_month');
    const [periodMode, setPeriodMode] = useState('monthly');
    
    const [filters, setFilters] = useState({
        date: initialRange.dateTo,
        dateFrom: initialRange.dateFrom,
        dateTo: initialRange.dateTo,
        month: initialMonth,
        year: initialYear,
        branch: ''
    });
    
    const months = REPORT_MONTHS;
    const pad = (value) => String(value).padStart(2, '0');
    const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

    const applyPeriodMode = (mode) => {
        setPeriodMode(mode);
        setReportType(mode === 'monthly' ? 'period' : 'as_of');
        setFilters(prev => {
            if (mode === 'monthly') {
                const range = getMonthDateRange(prev.year, prev.month);
                return { ...prev, date: range.dateTo, dateFrom: range.dateFrom, dateTo: range.dateTo };
            }
            if (mode === 'annual') {
                const range = getYearDateRange(prev.year);
                return { ...prev, date: range.dateTo, dateFrom: range.dateFrom, dateTo: range.dateTo };
            }
            return {
                ...prev,
                dateFrom: prev.dateFrom || initialRange.dateFrom,
                dateTo: prev.dateTo || prev.date || initialRange.dateTo,
                date: prev.dateTo || prev.date || initialRange.dateTo,
            };
        });
    };

    const handlePeriodMonthChange = (month) => {
        setPeriodMode('monthly');
        setReportType('period');
        setFilters(prev => {
            const range = getMonthDateRange(prev.year, month);
            return { ...prev, month, date: range.dateTo, dateFrom: range.dateFrom, dateTo: range.dateTo };
        });
    };

    const handlePeriodYearChange = (year) => {
        setFilters(prev => {
            if (periodMode === 'annual') {
                const range = getYearDateRange(year);
                return { ...prev, year, date: range.dateTo, dateFrom: range.dateFrom, dateTo: range.dateTo };
            }
            const range = getMonthDateRange(year, prev.month);
            return { ...prev, year, date: range.dateTo, dateFrom: range.dateFrom, dateTo: range.dateTo };
        });
    };

    const handleCustomDateChange = (field, value) => {
        setPeriodMode('custom');
        setReportType('as_of');
        setFilters(prev => ({ ...prev, [field]: value, date: field === 'dateTo' ? value : prev.date }));
    };

    const getTraceRange = () => {
        if (data?.period_start && data?.period_end) {
            return { dateFrom: data.period_start, dateTo: data.period_end };
        }
        if (reportType === 'ytd') {
            const dateTo = `${filters.year}-${pad(filters.month)}-${pad(lastDayOfMonth(filters.year, filters.month))}`;
            return { dateFrom: `${filters.year}-01-01`, dateTo };
        }
        if (reportType === 'as_of') {
            const dateFrom = filters.dateFrom || filters.date || filters.dateTo;
            const dateTo = filters.dateTo || filters.date || filters.dateFrom;
            return { dateFrom, dateTo };
        }
        const dateFrom = `${filters.year}-${pad(filters.month)}-01`;
        const dateTo = `${filters.year}-${pad(filters.month)}-${pad(lastDayOfMonth(filters.year, filters.month))}`;
        return { dateFrom, dateTo };
    };

    const accountRangeFromItems = (items = []) => {
        const codes = [...new Set(items.map(item => String(item.account_code || '').trim()).filter(Boolean))].sort();
        if (codes.length === 0) return null;
        return {
            from: codes[0],
            to: codes[codes.length - 1],
            label: codes.length === 1 ? codes[0] : `${codes[0]} to ${codes[codes.length - 1]}`,
        };
    };

    const traceSectionInGl = (label, items, amount) => {
        const range = accountRangeFromItems(items);
        if (!range) {
            toast.info(`No posted ${label.toLowerCase()} accounts to trace for this report.`);
            return;
        }
        const { dateFrom, dateTo } = getTraceRange();
        onTraceGl?.({
            sourceReport: 'Income Statement',
            label,
            accountFrom: range.from,
            accountTo: range.to,
            dateFrom,
            dateTo,
            amount,
            accountLabel: range.label,
            scopeLabel: viewingStudentName || 'Your workspace',
            nextAction: 'Review the filtered posted GL rows, then use Source Ref to open the original JV, AR/AP, or adjustment evidence.',
        });
    };

    const traceAccountInGl = (item, sectionLabel) => {
        const accountCode = String(item?.account_code || '').trim();
        if (!accountCode) {
            toast.info('This account has no account code to trace in General Ledger.');
            return;
        }
        const { dateFrom, dateTo } = getTraceRange();
        onTraceGl?.({
            sourceReport: 'Income Statement',
            label: `${sectionLabel}: ${item.account_name || accountCode}`,
            accountFrom: accountCode,
            accountTo: accountCode,
            dateFrom,
            dateTo,
            amount: item.current_balance ?? 0,
            accountLabel: accountCode,
            scopeLabel: viewingStudentName || 'Your workspace',
            nextAction: 'Review the account-level posted GL rows, then use Source Ref to open the original JV, AR/AP, or adjustment evidence.',
        });
    };
    
    // Process Income Statement
    const handleProcess = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            
            params.append('mode', reportType);
            
            if (reportType === 'period' || reportType === 'comparative' || reportType === 'ytd') {
                params.append('month', filters.month);
                params.append('year', filters.year);
            }
            
            if (reportType === 'comparative') {
                params.append('compare_type', compareType);
            }
            
            if (reportType === 'as_of') {
                if (filters.dateFrom && filters.dateTo) {
                    params.append('date_from', filters.dateFrom);
                    params.append('date_to', filters.dateTo);
                } else {
                    params.append('date', filters.date);
                }
            }
            
            if (filters.branch) params.append('branch', filters.branch);
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            
            const response = await axios.get(`${API}/income-statement?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setData(response.data);
            toast.success('Income Statement generated on this report tab. It uses posted GL entries for the selected period; source JVs/AR/AP records were not changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate Income Statement');
        } finally {
            setLoading(false);
        }
    };

    const handleExportPDF = () => {
        if (!data) return;
        setExporting(true);
        try {
            const result = generateIncomeStatementPDF(data, filters, reportType, compareType, fmt);
            if (result.success) {
                toast.success(`PDF exported: ${result.filename} (${(result.blobSize / 1024).toFixed(2)} KB). Export only; source records were not changed.`);
            } else {
                toast.error(`Failed to export PDF: ${result.error}`);
            }
        } catch (error) {
            toast.error('Failed to export PDF: ' + error.message);
        } finally {
            setExporting(false);
        }
    };
    
    // Render section
    const renderSection = (title, items, bgColor, borderColor, isExpense = false) => (
        <div className={`border ${borderColor} rounded-lg overflow-hidden mb-4`}>
            <div className={`${bgColor} px-4 py-2 font-semibold text-white flex items-center justify-between`}>
                <span>{title}</span>
                <span className="text-sm opacity-80">{(items || []).length} accounts</span>
            </div>
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr>
                        <th className="text-left px-4 py-2">Account Code</th>
                        <th className="text-left px-4 py-2">Account Name</th>
                        <th className="text-right px-4 py-2">Current</th>
                        <th className="text-right px-4 py-2">Trace</th>
                        {(reportType === 'comparative' || reportType === 'ytd') && (
                            <>
                                <th className="text-right px-4 py-2">Comparison</th>
                                <th className="text-right px-4 py-2">% Change</th>
                            </>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan={(reportType === 'comparative' || reportType === 'ytd') ? 6 : 4} className="px-4 py-4 text-center text-slate-400">
                                No {isExpense ? 'expenses' : 'revenues'} recorded
                            </td>
                        </tr>
                    ) : (
                        items.map((item) => (
                            <tr key={item.account_code} className="hover:bg-slate-50">
                                <td className="px-4 py-2 font-mono">{item.account_code}</td>
                                <td className="px-4 py-2">{item.account_name}</td>
                                <td className="px-4 py-2 text-right font-mono">
                                    {fmt(item.current_balance ?? 0)}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => traceAccountInGl(item, title)}
                                        data-testid={`trace-gl-${String(item.account_code || '').replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                                    >
                                        <BookOpen className="w-3.5 h-3.5 mr-1" /> Trace
                                    </Button>
                                </td>
                                {(reportType === 'comparative' || reportType === 'ytd') && (
                                    <>
                                        <td className="px-4 py-2 text-right font-mono text-slate-500">
                                            {fmt(item.comparison_balance || 0)}
                                        </td>
                                        <td className={`px-4 py-2 text-right font-mono ${
                                            (item.pct_change || 0) > 0 ? (isExpense ? 'text-red-600' : 'text-green-600') : 
                                            (item.pct_change || 0) < 0 ? (isExpense ? 'text-green-600' : 'text-red-600') : 'text-slate-400'
                                        }`}>
                                            {item.pct_change > 0 ? '+' : ''}{item.pct_change || 0}%
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
    
    return (
        <div className="space-y-6" data-testid="income-statement-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">Income Statement</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Revenue - Expenses = Net Income for ${viewingStudentName}` : 'Revenue - Expenses = Net Income'}
                    </p>
                </div>
            </div>
            {viewingStudentName && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="income-statement-student-scope">
                    Student review scope is active. This report uses posted revenue and expense GL entries from {viewingStudentName}'s workspace only.
                </div>
            )}
            <ReportWorkflowTrail
                title="Income Statement trail"
                testId="income-statement-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Uses posted revenue and expense entries only: JVs, AR/AP postings, and adjustment evidence inside the selected period.'
                    },
                    {
                        label: 'Where it appears',
                        value: 'After posting, the work remains in its source module and appears here through General Ledger for the matching period.'
                    },
                    {
                        label: 'How to verify',
                        value: 'Use Trace on revenue, expense, or an account row to open General Ledger, then use Source Ref to open the original evidence.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'If revenue or expense is missing, check source status first. Draft, saved, submitted, and returned work will not appear until posted.'
                    }
                ]}
            />
            
            {/* Filters Panel */}
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                <ReportPeriodSelector
                    mode={periodMode}
                    onModeChange={applyPeriodMode}
                    month={filters.month}
                    year={filters.year}
                    onMonthChange={handlePeriodMonthChange}
                    onYearChange={handlePeriodYearChange}
                    dateFrom={filters.dateFrom}
                    dateTo={filters.dateTo}
                    onDateFromChange={(value) => handleCustomDateChange('dateFrom', value)}
                    onDateToChange={(value) => handleCustomDateChange('dateTo', value)}
                    effectText="Posted revenue and expense GL entries inside this coverage are included in net income."
                    testId="income-statement-period"
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <Label className="text-sm">Branch / Outlet</Label>
                        <Input
                            placeholder="All branches"
                            value={filters.branch}
                            onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                </div>
                
                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleProcess}
                        disabled={loading}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                        data-testid="is-process-btn"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" /> Process
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => window.print()}
                        disabled={!data}
                    >
                        <Printer className="w-4 h-4 mr-2" /> Preview / Print
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleExportPDF}
                        disabled={!data || exporting}
                        data-testid="export-income-statement-pdf-btn"
                    >
                        <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting...' : 'Export PDF'}
                    </Button>
                </div>
            </div>
            
            {/* Results */}
            {data && (
                <div className="space-y-4">
                    {/* Report Header */}
                    <div className="bg-[#0F2A44] text-white rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Income Statement</h3>
                                <p className="text-white/70 text-sm">
                                    {reportType === 'period' && `For ${months.find(m => m.value === filters.month)?.label} ${filters.year}`}
                                    {reportType === 'comparative' && `${months.find(m => m.value === filters.month)?.label} ${filters.year} vs ${
                                        compareType === 'prev_month' ? 'Previous Month' : 'Same Month Last Year'
                                    }`}
                                    {reportType === 'ytd' && `YTD ${filters.year} (Jan - ${months.find(m => m.value === filters.month)?.label}) vs YTD ${filters.year - 1}`}
                                    {reportType === 'as_of' && `${data.period_start} to ${data.period_end}`}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-white/50">Generated</p>
                                <p className="text-sm">{new Date(data.generated_at).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="income-statement-where-it-appears">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">What changed</p>
                                <p className="mt-1 text-sm text-slate-700">The report was generated from posted revenue and expense GL entries only. No source record was edited.</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Where to find it</p>
                                <p className="mt-1 text-sm text-slate-700">This report tab shows net income. General Ledger shows the posted evidence behind each revenue or expense total.</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Report effect</p>
                                <p className="mt-1 text-sm text-slate-700">Matches GL/Trial Balance only when the same student scope and date coverage are used.</p>
                            </div>
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Next safe action</p>
                                <p className="mt-1 text-sm text-slate-700">Trace unusual revenue or expenses to GL, then open Source Ref before preparing any correction JV.</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Sections */}
                    {renderSection('REVENUES', data.sections?.revenues || [], 'bg-emerald-600', 'border-emerald-200')}
                    {renderSection('EXPENSES', data.sections?.expenses || [], 'bg-red-600', 'border-red-200', true)}

                    <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-testid="income-statement-gl-trace-actions">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="font-semibold text-indigo-900">Trace this report in General Ledger</p>
                                <p className="text-sm text-indigo-700">
                                    Open posted ledger evidence for revenue or expense totals, then use Source Ref to find the original JV, AR/AP, or adjustment evidence.
                                </p>
                                {!(data.sections?.revenues || []).length && !(data.sections?.expenses || []).length && (
                                    <p className="text-xs text-indigo-700 mt-1">
                                        No revenue or expense GL accounts were found for this selected period. Change the report date range or post source records before tracing.
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => traceSectionInGl('Total Revenue', data.sections?.revenues || [], data.totals?.total_revenue ?? 0)}
                                    disabled={!(data.sections?.revenues || []).length}
                                    data-testid="trace-income-revenue-gl"
                                >
                                    <BookOpen className="w-4 h-4 mr-2" /> {(data.sections?.revenues || []).length ? 'Revenue GL' : 'No Revenue GL'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => traceSectionInGl('Total Expenses', data.sections?.expenses || [], data.totals?.total_expenses ?? 0)}
                                    disabled={!(data.sections?.expenses || []).length}
                                    data-testid="trace-income-expenses-gl"
                                >
                                    <BookOpen className="w-4 h-4 mr-2" /> {(data.sections?.expenses || []).length ? 'Expense GL' : 'No Expense GL'}
                                </Button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Net Income Summary */}
                    <div className={`rounded-lg p-6 ${data.is_profit ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <p className="text-sm text-slate-500">Total Revenue</p>
                                <p className="text-2xl font-bold text-emerald-600">{fmt(data.totals?.total_revenue ?? 0)}</p>
                                {(reportType === 'comparative' || reportType === 'ytd') && data.totals?.comparison_total_revenue != null && (
                                    <p className="text-xs text-slate-400">vs {fmt(data.totals.comparison_total_revenue)}</p>
                                )}
                            </div>
                            <div className="text-center">
                                <p className="text-sm text-slate-500">Total Expenses</p>
                                <p className="text-2xl font-bold text-red-600">{fmt(data.totals?.total_expenses ?? 0)}</p>
                                {(reportType === 'comparative' || reportType === 'ytd') && data.totals?.comparison_total_expenses != null && (
                                    <p className="text-xs text-slate-400">vs {fmt(data.totals.comparison_total_expenses)}</p>
                                )}
                            </div>
                            <div className="text-center col-span-2">
                                <p className="text-sm text-slate-500">{data.is_profit ? 'Net Income' : 'Net Loss'}</p>
                                <p className={`text-3xl font-bold ${data.is_profit ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {data.is_profit ? '' : '('}{fmt(Math.abs(data.totals?.net_income ?? 0))}{data.is_profit ? '' : ')'}
                                </p>
                                {(reportType === 'comparative' || reportType === 'ytd') && data.totals?.comparison_net_income != null && (
                                    <div className="mt-1">
                                        <span className="text-xs text-slate-400">vs {fmt(data.totals.comparison_net_income)}</span>
                                        {data.totals?.net_income_pct_change != null && (
                                            <span className={`ml-2 text-xs font-medium ${
                                                data.totals.net_income_pct_change > 0 ? 'text-emerald-600' : 
                                                data.totals.net_income_pct_change < 0 ? 'text-red-600' : 'text-slate-400'
                                            }`}>
                                                ({data.totals.net_income_pct_change > 0 ? '+' : ''}{data.totals.net_income_pct_change}%)
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Visual indicator */}
                        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-center">
                            {data.is_profit ? (
                                <div className="flex items-center gap-2 text-emerald-700">
                                    <TrendingUp className="w-5 h-5" />
                                    <span className="font-medium">Profitable Period</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 text-red-700">
                                    <AlertCircle className="w-5 h-5" />
                                    <span className="font-medium">Loss Recorded</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Empty State */}
            {!data && !loading && (
                <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                    <TrendingUp className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-500">Click "Process" to generate Income Statement</p>
                    <p className="text-sm text-slate-400 mt-1">Data source: Posted GL entries only</p>
                </div>
            )}
        </div>
    );
}

// ================= AR SUMMARY TAB =================


export default IncomeStatementTab;
